import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiCalendarToday,
  mdiTextBoxOutline,
  mdiShimmer,
  mdiAccountGroupOutline,
  mdiTrendingUp,
  mdiShieldAlertOutline,
} from "@mdi/js";
import "./sidebar.css";
import { useApp } from "app/contexts/AppContext";
import SidebarCollapseButton from "../../../images/sidebar-collapse.svg";

const Sidebar = () => {
  const {
    device: { isMobile, isTablet },
    sidebar: { isCollapsed, toggle },
    theme: { isDarkMode },
  } = useApp();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  // Hide sidebar completely on mobile/tablet when collapsed
  if ((isMobile || isTablet) && isCollapsed) {
    return null;
  }

  const showFullLabel = isMobile || isTablet || !isCollapsed;
  const showTooltips = !isMobile && !isTablet && isCollapsed;

  // Helper component for conditional tooltips
  const ConditionalTooltip = ({ text, children }) => {
    if (!showTooltips) {
      return children;
    }

    return (
      <OverlayTrigger
        placement="right"
        overlay={<Tooltip>{text}</Tooltip>}
        delay={{ show: 300, hide: 100 }}
      >
        {children}
      </OverlayTrigger>
    );
  };

  return (
    <>
      {/* Overlay for mobile/tablet when sidebar is open */}
      {(isMobile || isTablet) && !isCollapsed && (
        <div className="sidebar-overlay" onClick={toggle} />
      )}

      <div
        className={`sidebar ${
          isMobile || isTablet
            ? "mobile"
            : isCollapsed
              ? "collapsed"
              : "expanded"
        } ${isDarkMode ? "dark" : ""}`}
      >
        <Nav className="flex-column pt-2">
          {/* <Nav.Item>
            <ConditionalTooltip text="Dashboard">
              <Link
                to="/"
                className={`nav-link ${isActive("/") ? "active" : ""}`}
                onClick={isMobile || isTablet ? toggle : undefined}
              >
                <House size={24} />
                {showFullLabel && <span>Dashboard</span>}
              </Link>
            </ConditionalTooltip>
          </Nav.Item> */}
          <Nav.Item>
            <ConditionalTooltip text="Job Calendar">
              <Link
                to="/job-calendar"
                className={`nav-link ${
                  isActive("/job-calendar") ? "active" : ""
                }`}
                onClick={isMobile || isTablet ? toggle : undefined}
              >
                <Icon
                  path={mdiCalendarToday}
                  size={1}
                  className="sidebar-icon"
                />
                {showFullLabel && (
                  <div className="d-flex align-items-end justify-content-between w-100">
                    <span>Job Calendar</span>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item>
          <Nav.Item className="soon">
            <ConditionalTooltip text="Reports">
              <Link
                to="/reports"
                className={`nav-link ${isActive("/reports") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  return false;
                }}
              >
                <Icon
                  path={mdiTextBoxOutline}
                  size={1}
                  className="sidebar-icon"
                />
                {showFullLabel && (
                  <div className="d-flex align-items-end justify-content-between w-100">
                    <span>Reports</span>
                    <small>Soon</small>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item>
          <Nav.Item>
            <ConditionalTooltip text="Dashboard">
              <Link
                to="/dashboard"
                className={`nav-link ${isActive("/") ? "active" : ""}`}
                onClick={isMobile || isTablet ? toggle : undefined}
              >
                <Icon path={mdiTrendingUp} size={1} className="sidebar-icon" />
                {showFullLabel && (
                  <div className="d-flex align-items-end justify-content-between w-100">
                    <span>Dashboard</span>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item>
          <div className="sidebar-divider" />

          <Nav.Item>
            <ConditionalTooltip text="Smart Templates">
              <Link
                to="/templates"
                className={`nav-link ${isActive("/templates") ? "active" : ""}`}
                onClick={isMobile || isTablet ? toggle : undefined}
              >
                <Icon path={mdiShimmer} size={1} className="sidebar-icon" />
                {showFullLabel && (
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <span>Smart Templates</span>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item>

          {/* <Nav.Item className="soon">
            <ConditionalTooltip text="Projects">
              <Link
                to="/projects"
                className={`nav-link ${isActive("/projects") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  return false;
                }}
              >
                <Grid className="sidebar-icon" size={24} />
                {showFullLabel && (
                  <div className="d-flex align-items-end justify-content-between w-100">
                    <span>Projects</span>
                    <small>Soon</small>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item> */}
          <Nav.Item>
            <ConditionalTooltip text="Agent Management">
              <Link
                to="/agent-management"
                className={`nav-link ${
                  isActive("/agent-management") ? "active" : ""
                }`}
                onClick={isMobile || isTablet ? toggle : undefined}
              >
                <Icon
                  path={mdiAccountGroupOutline}
                  size={1}
                  className="sidebar-icon"
                />
                {showFullLabel && (
                  <div className="d-flex align-items-end justify-content-between w-100">
                    <span>Agent Management</span>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item>
          <div className="sidebar-divider my-2" />

          <Nav.Item>
            <ConditionalTooltip text="Disclaimer Setting">
              <Link
                to="/disclaimer-setting"
                className={`nav-link ${
                  isActive("/disclaimer-setting") ? "active" : ""
                }`}
                onClick={isMobile || isTablet ? toggle : undefined}
              >
                <Icon
                  path={mdiShieldAlertOutline}
                  size={1}
                  className="sidebar-icon"
                />
                {showFullLabel && (
                  <div className="d-flex align-items-end justify-content-between w-100">
                    <span>Disclaimer Setting</span>
                  </div>
                )}
              </Link>
            </ConditionalTooltip>
          </Nav.Item>
        </Nav>
        <div className="sidebar-collapse-button">
          <div className="sidebar-divider" />
          <ConditionalTooltip text="Expand Sidebar">
            <button className="close-button" onClick={toggle}>
              <img
                className="sidebar-icon"
                src={SidebarCollapseButton}
                alt="Collapse Sidebar"
                style={{
                  transform: isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
              <span className="collapse-label">Collapse Sidebar</span>
            </button>
          </ConditionalTooltip>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
